/** Module B — panneau caractéristiques extérieures + refs + génération IA. */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Rocket, Loader2, Layers, ImagePlus, Box, SlidersHorizontal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const Field = ({ label, children }) => (
  <div>
    <label className="text-[9.5px] uppercase tracking-wider text-gray-500 block mb-1">{label}</label>
    {children}
  </div>
);
const inputCls = 'w-full h-8 px-2 rounded bg-black/40 border border-white/10 text-[12px] text-gray-100 focus:outline-none focus:border-amber-400/50';

export const ExteriorPanel = ({ project, mapsKey, onClose, onRefresh, onOverlay, onBeforeAfter }) => {
  const [cfg, setCfg] = useState({
    niveaux: 2, toiture: 'plate', facade_materiaux: '', facade_couleur: '',
    style: 'contemporain', environnement: '', ...(project.exterior_config || {}),
  });
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const glbRef = useRef(null);
  const pollRef = useRef(null);
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target ? e.target.value : e }));

  useEffect(() => () => clearInterval(pollRef.current), []);

  const uploadRefs = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        await axios.post(`${API}/map-projects/${project.map_project_id}/refs`, fd, opts);
      }
      await onRefresh();
      toast.success(`${files.length} référence(s) importée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import échoué'); }
    finally { setUploading(false); }
  };

  const uploadGlb = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/map-projects/${project.map_project_id}/glb`, fd, opts);
      await onRefresh();
      toast.success('Modèle 3D importé — figez la zone pour le positionner');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import .glb échoué'); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.patch(`${API}/map-projects/${project.map_project_id}`, { exterior_config: cfg }, opts);
      const r = await axios.post(`${API}/map-projects/${project.map_project_id}/generate-exterior`,
        { maps_key: mapsKey }, opts);
      const rid = r.data.render_id;
      toast.loading('Génération du bâtiment extérieur…', { id: rid });
      pollRef.current = setInterval(async () => {
        const doc = await onRefresh();
        const rend = (doc?.renders || []).find((x) => x.render_id === rid);
        if (rend?.status === 'completed') {
          clearInterval(pollRef.current); setGenerating(false);
          toast.success('Rendu extérieur prêt', { id: rid });
        } else if (rend?.status === 'failed') {
          clearInterval(pollRef.current); setGenerating(false);
          toast.error(`Échec : ${rend.error || '?'}`, { id: rid });
        }
      }, 4000);
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || 'Lancement impossible');
    }
  };

  const renders = (project.renders || []).filter((r) => r.status === 'completed').reverse();

  return (
    <aside className="absolute top-4 right-4 bottom-5 z-[520] w-[330px] rounded-xl flex flex-col overflow-hidden"
           style={{ background: 'rgba(10,10,13,0.92)', backdropFilter: 'blur(12px)', border: '1px solid rgba(217,160,91,0.3)' }}
           data-testid="exterior-panel">
      <div className="px-3.5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--c-gold)' }}>
            Génération extérieure
          </span>
          <p className="text-[11px] text-gray-400 truncate max-w-[220px]">{project.name}</p>
        </div>
        <button type="button" onClick={onClose} data-testid="exterior-panel-close">
          <X className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3.5 space-y-3.5">
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Niveaux">
            <input type="number" min={1} max={12} value={cfg.niveaux} onChange={set('niveaux')}
                   className={inputCls} data-testid="ext-niveaux-input" />
          </Field>
          <Field label="Toiture">
            <select value={cfg.toiture} onChange={set('toiture')} className={inputCls} data-testid="ext-toiture-select">
              <option value="plate">Toit plat</option>
              <option value="deux_pans">2 pans</option>
              <option value="quatre_pans">4 pans</option>
              <option value="monopente">Monopente</option>
            </select>
          </Field>
        </div>
        <Field label="Matériaux de façade">
          <input value={cfg.facade_materiaux} onChange={set('facade_materiaux')} placeholder="enduit blanc, bardage bois, pierre…"
                 className={inputCls} data-testid="ext-materiaux-input" />
        </Field>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Teinte">
            <input value={cfg.facade_couleur} onChange={set('facade_couleur')} placeholder="blanc cassé…"
                   className={inputCls} data-testid="ext-couleur-input" />
          </Field>
          <Field label="Style">
            <input value={cfg.style} onChange={set('style')} className={inputCls} data-testid="ext-style-input" />
          </Field>
        </div>
        <Field label="Environnement / abords">
          <textarea value={cfg.environnement} onChange={set('environnement')} rows={2}
                    placeholder="jardin paysager, allée gravillonnée, haie…"
                    className="w-full px-2 py-1.5 rounded bg-black/40 border border-white/10 text-[12px] text-gray-100 focus:outline-none focus:border-amber-400/50"
                    data-testid="ext-env-input" />
        </Field>

        {/* Références visuelles */}
        <div className="rounded-lg border border-dashed border-white/15 p-2.5 text-center cursor-pointer hover:border-amber-400/40 transition-colors"
             onClick={() => fileRef.current?.click()} data-testid="ext-refs-dropzone"
             onDragOver={(e) => e.preventDefault()}
             onDrop={(e) => { e.preventDefault(); uploadRefs([...e.dataTransfer.files].filter((f) => f.type.startsWith('image/'))); }}>
          <ImagePlus className="w-4 h-4 mx-auto mb-1 text-gray-500" />
          <p className="text-[10.5px] text-gray-400">Déposez vos images de référence (style, matériaux)</p>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                 onChange={(e) => uploadRefs([...e.target.files])} />
        </div>
        {(project.refs || []).length > 0 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="ext-refs-thumbs">
            {project.refs.map((r) => (
              <img key={r.ref_id} alt={r.filename}
                   src={`${API}/map-projects/${project.map_project_id}/refs/${r.ref_id}/image`}
                   className="w-12 h-12 object-cover rounded border border-white/10" />
            ))}
          </div>
        )}

        {/* Import GLB */}
        <button type="button" onClick={() => glbRef.current?.click()} disabled={uploading}
                className="w-full h-8 rounded-lg text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                style={{ background: 'rgba(94,234,212,0.1)', color: '#5EEAD4', border: '1px solid rgba(94,234,212,0.3)' }}
                data-testid="ext-glb-import-btn">
          <Box className="w-3.5 h-3.5" />
          {project.glb_path ? `Remplacer le modèle 3D (${project.glb_filename || '.glb'})` : 'Importer un modèle 3D (.glb)'}
        </button>
        <input ref={glbRef} type="file" accept=".glb,.gltf" className="hidden"
               onChange={(e) => e.target.files[0] && uploadGlb(e.target.files[0])} />

        <button type="button" onClick={generate} disabled={generating}
                className="w-full h-10 rounded-lg text-[12.5px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #D9A05B, #B9803F)', color: '#0A0A0D' }}
                data-testid="ext-generate-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          Générer le bâtiment extérieur
        </button>

        {/* Rendus */}
        {renders.length > 0 && (
          <div className="space-y-2" data-testid="ext-renders-list">
            <span className="text-[9.5px] uppercase tracking-wider text-gray-500">Rendus ({renders.length})</span>
            {renders.map((r) => (
              <div key={r.render_id} className="rounded-lg overflow-hidden border border-white/10">
                <img alt="rendu extérieur"
                     src={`${API}/map-projects/${project.map_project_id}/renders/${r.render_id}/image`}
                     className="w-full block" />
                <div className="flex">
                  <button type="button" onClick={() => onOverlay(r.render_id)}
                          className="flex-1 h-8 text-[11px] font-semibold inline-flex items-center justify-center gap-1.5"
                          style={{ background: 'rgba(217,160,91,0.14)', color: 'var(--c-gold)' }}
                          data-testid={`ext-overlay-btn-${r.render_id}`}>
                    <Layers className="w-3 h-3" /> Superposer
                  </button>
                  <button type="button" onClick={() => onBeforeAfter(r.render_id)}
                          className="flex-1 h-8 text-[11px] font-semibold inline-flex items-center justify-center gap-1.5"
                          style={{ background: 'rgba(147,197,253,0.12)', color: '#93C5FD', borderLeft: '1px solid rgba(255,255,255,0.08)' }}
                          data-testid={`ext-beforeafter-btn-${r.render_id}`}>
                    <SlidersHorizontal className="w-3 h-3" /> Avant / Après
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {uploading && <p className="text-[10px] text-gray-500 flex items-center gap-1"><Upload className="w-3 h-3 animate-pulse" /> Import en cours…</p>}
      </div>
    </aside>
  );
};
